import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Logo } from '../components/Logo';
import { BRAND } from '../config/brand';

export function PartnerProgramme() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [about, setAbout] = useState('');
  const [recruitOthers, setRecruitOthers] = useState(false);
  const [source, setSource] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!fullName.trim() || !email.trim()) {
      setError('Name and email are needed.');
      return;
    }
    setSubmitting(true);
    try {
      const { error: insertError } = await supabase
        .from('partner_interest')
        .insert({
          full_name: fullName.trim(),
          email: email.trim().toLowerCase(),
          phone: phone.trim() || null,
          about: about.trim() || null,
          recruit_others: recruitOthers,
          source: source.trim() || null,
        });
      if (insertError) throw insertError;
      setSubmitted(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="partner-shell">
      <header className="partner-header">
        <Link to="/" className="partner-brand">
          <Logo size={40} decorative />
          <span>Safe and Sorted</span>
        </Link>
        <a
          href="https://affiliate.safeandsorted.com"
          target="_blank"
          rel="noopener noreferrer"
          className="partner-header-link"
          style={{
            marginLeft: 'auto',
            fontSize: 13,
            color: '#475569',
            textDecoration: 'none',
            fontWeight: 600,
            padding: '6px 12px',
            border: '1px solid #CBD5E1',
            borderRadius: 6,
          }}
        >
          Already a partner? Sign in →
        </a>
      </header>

      <main className="partner-main">
        <h1 className="partner-title">Affiliate programme</h1>
        <p className="partner-lede">
          Earn money introducing builders to Safe and Sorted. No fee to join, no time
          commitment, no targets to hit.
        </p>

        <div className="partner-deal">
          <p className="partner-deal-section">Year 1</p>
          <div className="partner-deal-row">
            <span className="partner-deal-amount">£20</span>
            <span className="partner-deal-label">sign-up bonus for every new customer</span>
          </div>
          <div className="partner-deal-row">
            <span className="partner-deal-amount">20%</span>
            <span className="partner-deal-label">recurring commission for the first 12 months</span>
          </div>
          <div className="partner-deal-row">
            <span className="partner-deal-amount">+ 5%</span>
            <span className="partner-deal-label">override on every other affiliate you bring on board</span>
          </div>

          <p className="partner-deal-section">Year 2 onwards (for as long as they stay)</p>
          <div className="partner-deal-row">
            <span className="partner-deal-amount">5%</span>
            <span className="partner-deal-label">lifetime residual on every customer you brought on — they keep paying you while they keep paying us</span>
          </div>
        </div>

        <h2 className="partner-h2">What you get when we launch</h2>
        <ul className="partner-list">
          <li>A unique referral link and QR code — anyone who signs up through it is yours.</li>
          <li>A printable A5 leaflet and business-card design with your name and code on, ready to take into builders' merchants, sites and trade events.</li>
          <li>A dashboard showing every signup, this month's commission and lifetime earnings.</li>
          <li>Monthly payouts via Stripe direct to your bank.</li>
        </ul>

        <h2 className="partner-h2">Who this works for</h2>
        <ul className="partner-list">
          <li><strong>Retired / semi-retired construction professionals</strong> — H&S consultants, ex-site managers, ex-CDM coordinators. You already know who needs this.</li>
          <li><strong>Self-employed sales reps</strong> who already call on builders, plant hire firms, builders' merchants.</li>
          <li><strong>Anyone with a network</strong> in construction — toolbox talk presenters, training providers, trade-show organisers.</li>
        </ul>

        <h2 className="partner-h2">The maths on a typical month</h2>
        <p className="partner-body">
          Sign up four small builders on Solo (£19/mo) → £80 in sign-up bounties + £15.20/mo
          recurring. Do that four months in a row and you're at £320 in bounties + £61/mo
          recurring, growing every month. Recruit one other affiliate who does the same →
          another £15-30/mo override on top.
        </p>
        <p className="partner-body">
          <strong>And it doesn't stop.</strong> When Year 1 wraps, every customer you brought
          on still pays you 5% for as long as they stay with us. Build a base of 30 customers
          and that's £30-60/mo of residual income year-on-year — without lifting a finger.
        </p>

        <h2 className="partner-h2">When does it go live?</h2>
        <p className="partner-body">
          We're finalising the maths and the dashboard. Drop your details below and we'll
          email you the moment you can claim a code and start. First-come, first-served on
          territories where we've got more interest than spaces.
        </p>

        <h2 className="partner-h2">The small print — straight up</h2>
        <p className="partner-body">
          We want this to be a fair, long-term arrangement. So we're transparent about
          where the guarantees end:
        </p>
        <ul className="partner-list">
          <li><strong>Year 1 commission and your £20 bonus are guaranteed.</strong> Once you've closed a customer, that money is yours regardless of what happens to the company.</li>
          <li><strong>The 5% Year 2+ residual is a loyalty bonus, not a contractual right.</strong> If the business is sold, wound down, or the affiliate programme is fundamentally changed, we reserve the right to end the residual on 90 days' written notice. Year 1 earnings and bounties already due remain payable in full.</li>
          <li><strong>We won't game it.</strong> The residual is there to keep good affiliates engaged. Pulling it without genuine cause would tank the programme — we don't plan to do that. Full T&Cs when we go live.</li>
        </ul>

        {submitted ? (
          <div className="partner-success card">
            <h3 style={{ marginTop: 0 }}>You're on the list.</h3>
            <p style={{ margin: 0 }}>
              Thanks {fullName.split(' ')[0]}. We'll email <strong>{email}</strong> the moment
              we go live. If you've got someone in mind already, hold the conversation —
              you'll be live within a few weeks.
            </p>
          </div>
        ) : (
          <form className="partner-form" onSubmit={onSubmit}>
            <h2 className="partner-h2" style={{ marginTop: 0 }}>Join the waiting list</h2>

            <div className="field">
              <label htmlFor="pp-name">Your name *</label>
              <input id="pp-name" type="text" required value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="pp-email">Email *</label>
              <input id="pp-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="pp-phone">Phone (optional)</label>
              <input id="pp-phone" type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="pp-about">Tell us about you</label>
              <textarea id="pp-about" rows={4}
                placeholder="e.g. Retired CDM coordinator in Yorkshire, still know most of the builders within 30 miles."
                value={about} onChange={(e) => setAbout(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="pp-source">How did you hear about us?</label>
              <input id="pp-source" type="text" placeholder="Family, friend, builders' merchant…" value={source} onChange={(e) => setSource(e.target.value)} />
            </div>
            <label className="check-row" style={{ marginBottom: 12 }}>
              <input type="checkbox" checked={recruitOthers} onChange={(e) => setRecruitOthers(e.target.checked)} />
              <span>I'd like to recruit other affiliates too — that's the 5% override level.</span>
            </label>

            {error && <div className="banner banner-error" style={{ marginBottom: 12 }}>{error}</div>}

            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Adding you…' : 'Join the waiting list →'}
            </button>
            <p className="muted" style={{ fontSize: 12, marginTop: 10, textAlign: 'center' }}>
              We'll only use these details to email you about the affiliate programme. No spam.
            </p>
          </form>
        )}

        <p className="muted" style={{ fontSize: 11, textAlign: 'center', marginTop: 32, lineHeight: 1.5 }}>
          {BRAND.name} is a trading name of <strong>{BRAND.legalEntity}</strong> (company no. {BRAND.companyNumber}),
          registered in {BRAND.companyJurisdiction}.<br />Registered office: {BRAND.registeredOffice}.
        </p>
      </main>
    </div>
  );
}
